import { motion } from "motion/react";
import { useState, type ReactNode } from "react";
import { cn } from "web-design-system";

const MAX_STACK_LAYERS = 2;
const STACK_TRANSITION = { duration: 0.4, ease: [0.4, 0, 0.2, 1] as const };

const STACK_PEEK_LAYERS = [
  "absolute inset-x-4 bottom-0 z-0",
  "absolute inset-x-2 bottom-1.5 z-10",
] as const;

function getStackPeekLayers(stackLayers: number) {
  if (stackLayers >= 2) return [...STACK_PEEK_LAYERS];
  if (stackLayers === 1) return ["absolute inset-x-2 bottom-0 z-10"];
  return [];
}

function StatusCardFrame({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "w-full overflow-hidden rounded-lg border border-border",
        className
      )}
    >
      {children}
    </div>
  );
}

export function StackedStatusCards({
  children,
  className,
  framed = true,
  expanded: expandedProp,
  hoverToExpand = true,
}: {
  children: ReactNode;
  className?: string;
  framed?: boolean;
  expanded?: boolean;
  hoverToExpand?: boolean;
}) {
  const [expandedState, setExpandedState] = useState(false);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : expandedState;
  const cards = (Array.isArray(children) ? children : [children]).filter(
    Boolean
  );

  if (!cards.length) return null;

  const wrapCard = (card: ReactNode) =>
    framed ? <StatusCardFrame>{card}</StatusCardFrame> : card;

  if (cards.length === 1) {
    return <div className={cn("w-full", className)}>{wrapCard(cards[0])}</div>;
  }

  const stackLayers = Math.min(cards.length - 1, MAX_STACK_LAYERS);
  const peekLayers = getStackPeekLayers(stackLayers);

  return (
    <div
      className={cn("relative w-full", framed && "bg-secondary", className)}
      onMouseEnter={
        hoverToExpand && !isControlled
          ? () => setExpandedState(true)
          : undefined
      }
      onMouseLeave={
        hoverToExpand && !isControlled
          ? () => setExpandedState(false)
          : undefined
      }
    >
      {peekLayers.map((position, index) => (
        <motion.div
          key={index}
          aria-hidden
          initial={false}
          animate={{
            opacity: expanded ? 0 : 0.6,
            y: expanded ? 8 : 0,
            scale: expanded ? 0.96 : 1,
          }}
          transition={STACK_TRANSITION}
          className={cn(
            position,
            "pointer-events-none h-4 origin-bottom rounded-lg border border-border bg-card",
            expanded && "invisible"
          )}
        />
      ))}
      <div
        className={cn(
          "relative z-20 flex flex-col transition-[gap] duration-[400ms] ease-out",
          expanded ? "gap-2" : "gap-0"
        )}
      >
        {cards.map((card, index) => (
          <motion.div
            key={index}
            initial={false}
            animate={{
              opacity: expanded || index === 0 ? 1 : 0,
              y: expanded
                ? 0
                : index > 0
                  ? Math.min(index, MAX_STACK_LAYERS) * 8
                  : 0,
              scale:
                expanded || index === 0 ? 1 : Math.max(1 - index * 0.05, 0.9),
            }}
            transition={{
              ...STACK_TRANSITION,
              delay: expanded && index > 0 ? index * 0.05 : 0,
            }}
            className={cn(
              "grid origin-top transition-[grid-template-rows,margin-bottom] duration-[400ms] ease-out",
              expanded || index === 0 ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              index === 0 && !expanded && stackLayers >= 1 && "mb-3"
            )}
            style={{
              transitionDelay:
                expanded && index > 0 ? `${index * 50}ms` : undefined,
            }}
          >
            <div className="min-h-0 overflow-hidden">{wrapCard(card)}</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
